import { APIService } from "@/core/api/APIService";
import { QueryBuilder } from "@/core/api/QueryBuilder";
import {
  StoreGroupDTO,
  StoreGroupDTOSchema,
  StoreGroupMemberDTO,
} from "./StoreGroupDTO";
import {
  mapStoreGroup,
  mapStoreGroups,
  mapStoreGroupMembers,
} from "./StoreGroupMapper";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";

/**
 * List query parameters
 */
export interface ListOptions {
  filters?: Record<string, unknown>; // Flexible filter structure for testing and usage
  limit?: number;
  lastItem?: string;
  sorting?: Record<string, "asc" | "desc">;
}

/**
 * Service for managing store groups
 * Encapsulates all API calls related to store groups with proper DTO handling and mapping
 */
export class StoreGroupService {
  constructor(
    private readonly api: APIService,
    private readonly queryBuilder: QueryBuilder,
  ) {}

  /**
   * Fetch all store groups globally (for dictionary/select components)
   */
  async list(
    options?: ListOptions,
  ): Promise<{ items: ReadStoreGroup[]; count?: number; lastItem?: string }> {
    const query = this.buildQuery(options);
    const response = await this.api.request<{
      items: StoreGroupDTO[];
      count?: number;
      last_item?: string;
    }>(
      {
        path: `/store-groups`,
        query,
      },
      undefined, // Response shape varies; handle inline
    );

    const items = Array.isArray(response) ? response : response?.items || [];
    const dtos = items as StoreGroupDTO[];

    return {
      items: mapStoreGroups(dtos),
      count: response?.count,
      lastItem: response?.last_item,
    };
  }

  /**
   * Fetch a paginated list of store groups for a project
   */
  async listByProject(
    projectId: string,
    options?: ListOptions,
  ): Promise<{ items: ReadStoreGroup[]; count?: number; lastItem?: string }> {
    const query = this.buildQuery(options);
    const response = await this.api.request<{
      items: StoreGroupDTO[];
      count?: number;
      last_item?: string;
    }>(
      {
        path: `/projects/${projectId}/store-groups`,
        query,
      },
      undefined, // Response shape varies; handle inline
    );

    const items = Array.isArray(response) ? response : response?.items || [];
    const dtos = items as StoreGroupDTO[];

    return {
      items: mapStoreGroups(dtos),
      count: response?.count,
      lastItem: response?.last_item,
    };
  }

  /**
   * Fetch a single store group by ID
   */
  async getStoreGroup(id: string): Promise<ReadStoreGroup> {
    const dto = await this.api.request<StoreGroupDTO>(
      { path: `/store-groups/${id}` },
      StoreGroupDTOSchema,
    );
    return mapStoreGroup(dto);
  }

  /**
   * Fetch stores within a store group with pagination
   */
  async listStoresInGroup(
    groupId: string,
    options?: ListOptions,
  ): Promise<{
    items: ReadStoreGroupMember[];
    count?: number;
    lastItem?: string;
  }> {
    const query = this.buildQuery(options);
    const response = await this.api.request<{
      items: StoreGroupMemberDTO[];
      count?: number;
      last_item?: string;
    }>(
      {
        path: `/store-groups/${groupId}/stores`,
        query,
      },
      undefined,
    );

    const items = Array.isArray(response) ? response : response?.items || [];
    const dtos = items as StoreGroupMemberDTO[];

    return {
      items: mapStoreGroupMembers(dtos),
      count: response?.count,
      lastItem: response?.last_item,
    };
  }

  /**
   * Create a new store group
   * NOTE: API returns wrapped response {ok: string, data: StoreGroupDTO}
   */
  async createStoreGroup(data: WriteStoreGroup): Promise<ReadStoreGroup> {
    const response = await this.api.request<{
      ok: string;
      data: StoreGroupDTO;
    }>({
      path: `/store-groups`,
      method: "POST",
      body: data,
    });
    const parsed = StoreGroupDTOSchema.safeParse(response.data);
    if (!parsed.success) {
      throw new Error(`Invalid store group response: ${parsed.error.message}`);
    }
    return mapStoreGroup(parsed.data);
  }

  /**
   * Update a store group
   * NOTE: API returns wrapped response {ok: string, data: StoreGroupDTO}
   */
  async updateStoreGroup(
    id: string,
    data: WriteStoreGroup,
  ): Promise<ReadStoreGroup> {
    const response = await this.api.request<{
      ok: string;
      data: StoreGroupDTO;
    }>({
      path: `/store-groups/${id}`,
      method: "PATCH",
      body: data,
    });
    const parsed = StoreGroupDTOSchema.safeParse(response.data);
    if (!parsed.success) {
      throw new Error(`Invalid store group response: ${parsed.error.message}`);
    }
    return mapStoreGroup(parsed.data);
  }

  /**
   * Update stores in a group (add/remove stores)
   * NOTE: The /stores endpoint expects the FULL WriteStoreGroup payload
   * (name, description, stores) not just the stores part
   */
  async updateGroupStores(
    id: string,
    data: WriteStoreGroup,
  ): Promise<ReadStoreGroup> {
    const response = await this.api.request<{
      ok: string;
      data: StoreGroupDTO;
    }>({
      path: `/store-groups/${id}/stores`,
      method: "PATCH",
      body: data,
    });
    const parsed = StoreGroupDTOSchema.safeParse(response.data);
    if (!parsed.success) {
      throw new Error(`Invalid store group response: ${parsed.error.message}`);
    }
    return mapStoreGroup(parsed.data);
  }

  /**
   * Delete a store group
   */
  async deleteStoreGroup(id: string): Promise<void> {
    await this.api.request({
      path: `/store-groups/${id}`,
      method: "DELETE",
    });
  }

  /**
   * Build query parameters from filter options
   */
  private buildQuery(options?: ListOptions): Record<string, string> {
    const query: Record<string, string> = {};

    if (options?.filters) {
      const filterQuery = this.queryBuilder.build(options.filters);
      Object.assign(query, filterQuery);
    }

    if (options?.limit) {
      query.limit = String(options.limit);
    }

    if (options?.lastItem) {
      query.last_item = options.lastItem;
    }

    if (options?.sorting) {
      for (const [field, direction] of Object.entries(options.sorting)) {
        // Match legacy API format: sort=fieldName or sort=-fieldName
        const sortValue = direction === "desc" ? `-${field}` : field;
        query.sort = sortValue;
      }
    }

    return query;
  }
}
